import L from 'leaflet'

import type { MapConfig } from '../api/types'
import { kacheloptionen } from './kartenquelle'

/**
 * A Leaflet map that stands still, for a picture (1.112.0, review).
 *
 * The day card's route (`tagesroute.ts`), the point's map in the book
 * (`Punktkarte.tsx`) and the book's route (`Buchkarte.tsx`) each built the
 * same map: no gestures, the tiles with `crossOrigin` so that the still
 * image may copy them, and a promise that settles when the tiles are there
 * -- or after twelve seconds, so that a tile server that keeps silent does
 * not block the print for ever. Three copies, with the twelve seconds
 * unnamed in each; what differs stays with the caller (the attribution,
 * whether the map may animate, what is drawn on it).
 */

/** How long a picture waits for the tiles before it is taken as it is. */
export const TILE_WAIT_MS = 12_000

export interface StillMap {
  map: L.Map
  layer: L.TileLayer
  /** Settles when the visible tiles are there, or after `TILE_WAIT_MS`. */
  ready: Promise<void>
  /** Removes the map and the timer behind `ready`. */
  dispose: () => void
}

export function stillMap(
  container: HTMLElement,
  source: MapConfig,
  { attribution = true, animate = true }: { attribution?: boolean; animate?: boolean } = {},
): StillMap {
  const map = L.map(container, {
    zoomControl: false,
    attributionControl: attribution,
    // Nothing of this is needed: the picture is a still.
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
    keyboard: false,
    // Without animation where the picture is taken as soon as the tiles
    // are there: a map still gliding into place would be copied mid-flight.
    ...(animate ? {} : { zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }),
  })
  // `crossOrigin`, so that the still image may copy the tiles (`mapImage.ts`).
  const layer = L.tileLayer(source.tile_url, { ...kacheloptionen(source), crossOrigin: 'anonymous' })
  let timer: number | undefined
  const ready = new Promise<void>((resolve) => {
    let done = false
    const settle = () => {
      if (done) return
      done = true
      window.clearTimeout(timer)
      resolve()
    }
    layer.on('load', settle)
    timer = window.setTimeout(settle, TILE_WAIT_MS)
  })
  layer.addTo(map)
  return {
    map,
    layer,
    ready,
    dispose: () => {
      window.clearTimeout(timer)
      map.remove()
    },
  }
}
